import React, { useState } from 'react'

import { Popover, SxProps } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Chip, { ChipProps } from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import ListItemIcon from 'components_new/atoms/List/ListItemIcon'
import ListItemSecondaryAction from 'components_new/atoms/List/ListItemSecondaryAction'
import ListItemText from 'components_new/atoms/List/ListItemText'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import { Option } from '../SelectSearch'

interface ChipSelectProps {
  color?: ChipProps['color']
  disabled?: boolean
  icon?: ChipProps['icon']
  onChange: (value: Option) => void
  options: Option[]
  optionsTitle?: string
  value: Option['id']
  size?: ChipProps['size']
  sx?: SxProps
  tooltip?: string
}

const ChipSelect = React.forwardRef((props: ChipSelectProps, ref: any) => {
  const {
    color,
    disabled,
    icon,
    onChange,
    options,
    optionsTitle,
    size,
    value,
    sx,
    tooltip
  } = props

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLAnchorElement | null>(
    null
  )
  const menuOpen = Boolean(menuAnchorEl)

  const icons = options.some((option) => option.icon)
  const selectedOption = options.find((option) => option.id === value)

  const handleClose = () => {
    setMenuAnchorEl(null)
  }

  const handleClick = (option: Option) => {
    onChange(option)
    handleClose()
  }

  return (
    <>
      <Tooltip title={tooltip}>
        <Chip
          clickable={!disabled}
          color={selectedOption?.icon ? undefined : color}
          deleteIcon={disabled ? undefined : <Icon name="UnfoldMore" />}
          icon={
            selectedOption?.icon ? (
              <Icon color={selectedOption.color} name={selectedOption.icon} />
            ) : (
              icon
            )
          }
          label={selectedOption?.title}
          onClick={
            disabled
              ? undefined
              : (e: any) => {
                  e.preventDefault()
                  e.stopPropagation()

                  setMenuAnchorEl(e.currentTarget)
                }
          }
          onDelete={disabled ? undefined : () => {}}
          onMouseDown={(e) => e.stopPropagation()}
          ref={ref}
          size={size}
          sx={{
            ...sx,
            ['.MuiChip-deleteIcon']: {
              pointerEvents: 'none'
            }
          }}
        />
      </Tooltip>

      {/*-- popover --*/}
      <Popover
        anchorEl={menuAnchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={(event) => {
          (event as any).stopPropagation()

          handleClose()
        }}
        onMouseDown={(e) => e.stopPropagation()}
        open={menuOpen}
        disableScrollLock
        slotProps={{
          paper: {
            onClick: (event: any) => event?.stopPropagation(),
            onMouseDown: (event: any) => event?.stopPropagation()
          }
        }}
        transformOrigin={{ horizontal: 0, vertical: -4 }}
      >
        <Box sx={{ p: 1 }}>
          {optionsTitle ? (
            <Text color="text.secondary" variant="caption">
              {optionsTitle}
            </Text>
          ) : null}

          {options.map((option) => {
            return (
              <MenuItem
                disableGutters={true}
                key={option.id}
                onClick={() => handleClick(option)}
                sx={{ pl: 1, pr: 1 + 4 }}
              >
                {icons ? (
                  <ListItemIcon>
                    {option.icon ? (
                      <Icon
                        color={option.color}
                        fontSize="small"
                        name={option.icon}
                      />
                    ) : null}
                  </ListItemIcon>
                ) : null}
                <ListItemText
                  primary={option.title}
                  secondary={option.body}
                  secondaryTypographyProps={{ color: 'text.secondary' }}
                />
                <ListItemSecondaryAction>
                  <Icon
                    color="primary"
                    fontSize="small"
                    name="Check"
                    sx={{ opacity: option.id === value ? 1 : 0 }}
                  />
                </ListItemSecondaryAction>
              </MenuItem>
            )
          })}
        </Box>
      </Popover>
    </>
  )
})

ChipSelect.displayName = 'ChipSelect'
export default ChipSelect
